import React, { useState, useEffect, useRef } from "react";
import { useChatbotAuth } from "./ChatbotAuthContext";
import API_BASE_URL from "./config";
import { isChatIconEnabled, isChatIconImageUrl, normalizeChatIcon } from "./chatIconUtils";
import { messageMatchesKeywords } from "./keywordUtils";
import "./Chatbot.css";

/** Cross-page widget persistence (iframe reloads / site navigation) */
const WIDGET_PERSIST_VERSION = 1;
const WIDGET_MAX_STORED_MESSAGES = 100;

function generateWidgetUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function widgetStorageKeys(botId) {
  const id = String(botId || "MAIN").toLowerCase();
  return {
    session: `weta_widget_v${WIDGET_PERSIST_VERSION}_sid_${id}`,
    state: `weta_widget_v${WIDGET_PERSIST_VERSION}_state_${id}`,
    visitor: `weta_widget_v${WIDGET_PERSIST_VERSION}_visitor_${id}`,
  };
}

function isWidgetEmbeddedContext(embeddedProp) {
  if (embeddedProp) return true;
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("embed") === "1") return true;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

function buildWidgetPersistPayload(state) {
  const toStore = (state.messages || []).slice(-WIDGET_MAX_STORED_MESSAGES);
  return {
    v: WIDGET_PERSIST_VERSION,
    conversationId: state.conversationId,
    messages: toStore,
    surveyCompleted: state.surveyCompleted,
    surveyStart: state.surveyStart,
    surveyResponses: state.surveyResponses,
    currentQuestionIndex: state.currentQuestionIndex,
    hasSentFirstChatMessage: state.hasSentFirstChatMessage,
    redirectMode: state.redirectMode,
    redirectStep: state.redirectStep,
    redirectData: state.redirectData,
    savedAt: Date.now(),
  };
}

function writeWidgetPersistPayload(botId, payload) {
  const { session, state: stateKey } = widgetStorageKeys(botId);
  localStorage.setItem(stateKey, JSON.stringify(payload));
  if (payload.conversationId) {
    localStorage.setItem(session, payload.conversationId);
  }
}

function readOrCreateWidgetSessionId(botId) {
  try {
    const { session } = widgetStorageKeys(botId);
    let sid = localStorage.getItem(session);
    const uuidRe =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!sid || !uuidRe.test(sid)) {
      sid = generateWidgetUUID();
      localStorage.setItem(session, sid);
    }
    return sid;
  } catch {
    return generateWidgetUUID();
  }
}

function readOrCreateVisitorId(botId) {
  try {
    const { visitor } = widgetStorageKeys(botId);
    let visitorId = localStorage.getItem(visitor);
    if (!visitorId || typeof visitorId !== "string") {
      visitorId = `visitor-${generateWidgetUUID()}`;
      localStorage.setItem(visitor, visitorId);
    }
    return visitorId;
  } catch {
    return `visitor-${generateWidgetUUID()}`;
  }
}

function isUncertainBotReply(text) {
  const value = String(text || "").toLowerCase();
  if (!value) return true;
  const uncertainPhrases = [
    "i don't know",
    "i do not know",
    "i'm not sure",
    "i am not sure",
    "don't have that",
    "do not have that",
    "not in my knowledge base",
    "unable to",
    "cannot provide",
    "no information",
  ];
  return uncertainPhrases.some((p) => value.includes(p));
}

function sanitizeSmartFollowUps(followUps, botReplyText) {
  if (!Array.isArray(followUps) || followUps.length === 0) return [];
  if (isUncertainBotReply(botReplyText)) return [];
  return followUps
    .map((s) => String(s || "").trim())
    .filter((s) => s.length >= 8 && s.length <= 140 && s.endsWith("?"))
    .slice(0, 4);
}

const languages = {
  en: { name: 'English', code: 'en-US' },
  ta: { name: 'Tamil', code: 'ta-IN' },
  fr: { name: 'French', code: 'fr-FR' },
  de: { name: 'German', code: 'de-DE' },
  ja: { name: 'Japanese', code: 'ja-JP' },
  ru: { name: 'Russian', code: 'ru-RU' },
  zh: { name: 'Chinese', code: 'zh-CN' },
  ko: { name: 'Korean', code: 'ko-KR' },
};

export default function Chatbot({ onSurveyToggle, onClose, embedded }) {
  const { initializeSession, trackActivity, sessionExpired, expiredMessage } = useChatbotAuth();
  const [messages, setMessages] = useState([]);  // Initialize empty, load dynamic message
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const botId = params.get("bot_id") || "MAIN";
    const urlSessionId = params.get("session_id");
    if (urlSessionId && typeof urlSessionId === "string" && urlSessionId.trim()) {
      try {
        localStorage.setItem(widgetStorageKeys(botId).session, urlSessionId.trim());
      } catch {}
      return urlSessionId.trim();
    }
    return readOrCreateWidgetSessionId(botId);
  });
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  const [surveyStart, setSurveyStart] = useState(false);
  const [surveyQuestions, setSurveyQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [surveyResponses, setSurveyResponses] = useState([]);
  const [hasShownValidationError, setHasShownValidationError] = useState(false);
  const [currentTextValue, setCurrentTextValue] = useState('');
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  const [botSettings, setBotSettings] = useState({
    bot_name: 'AI Customer Support',
    welcome_message: 'Hello 👋! I\'m your AI Assistant. To help me serve you better, I\'d love to learn more about you first.\n\nLet\'s start with a quick survey – it only takes 2 minutes and helps me understand your needs better.'
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [hasShownPopupInSession, setHasShownPopupInSession] = useState(false);
  const [audioCtx, setAudioCtx] = useState(null);
  const [hasAppendedExpiry, setHasAppendedExpiry] = useState(false);
  const popupVersionRef = useRef('');
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const [voiceOutputEnabled, setVoiceOutputEnabled] = useState(false);
  const synthRef = useRef(null);
  const spokenMessagesRef = useRef(new Set());
  const [hasSentFirstChatMessage, setHasSentFirstChatMessage] = useState(false);
  const [redirectMode, setRedirectMode] = useState(false);
  const [redirectStep, setRedirectStep] = useState(0); // 0: None, 1: Name, 2: Email, 3: Phone, 4: Done
  const [redirectData, setRedirectData] = useState({ user_name: '', user_email: '', user_phone: '' });
  const hydrationDoneRef = useRef(false);
  const persistTimerRef = useRef(null);
  const persistSnapshotRef = useRef({});
  const isEmbeddedWidget = isWidgetEmbeddedContext(embedded);

  persistSnapshotRef.current = {
    messages,
    conversationId,
    surveyCompleted,
    surveyStart,
    surveyResponses,
    currentQuestionIndex,
    hasSentFirstChatMessage,
    redirectMode,
    redirectStep,
    redirectData,
    settingsLoaded,
    botId: botSettings?.bot_id,
  };

  const flushWidgetStateNow = () => {
    if (!hydrationDoneRef.current) return;
    const snap = persistSnapshotRef.current;
    if (!snap.settingsLoaded) return;
    const urlParams = new URLSearchParams(window.location.search);
    const botId = urlParams.get("bot_id") || snap.botId || "MAIN";
    try {
      writeWidgetPersistPayload(botId, buildWidgetPersistPayload(snap));
    } catch (e) {
      console.warn("[Chatbot] Widget state flush failed:", e);
    }
  };

  const handleCloseClick = () => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
    flushWidgetStateNow();
    onClose?.();
  };

  function getWidgetApiCandidates(pathWithQuery) {
    const cleaned = String(pathWithQuery || "").replace(/^\//, "");
    const apiBase = String(API_BASE_URL || "").replace(/\/$/, "");
    const primary = `${apiBase}/api/widget/${cleaned}`;
    const out = [primary];
    if (!/\/chatbot$/i.test(apiBase)) {
      out.push(`${apiBase}/chatbot/api/widget/${cleaned}`);
    }
    return out;
  }

  async function fetchWidgetApiWithFallback(pathWithQuery, init) {
    const candidates = getWidgetApiCandidates(pathWithQuery);
    let lastResponse = null;
    let lastError = null;
    for (const url of candidates) {
      try {
        const response = await fetch(url, init);
        if (response.ok) return response;
        lastResponse = response;
      } catch (err) {
        lastError = err;
      }
    }
    if (lastResponse) return lastResponse;
    throw lastError || new Error("All widget API endpoints failed");
  }

  // Inform host widget script about selected frame size (small/medium/fullscreen).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sizeRaw = String(botSettings?.window_size || "medium").toLowerCase().replace(/[\s_-]/g, "");
    const size = sizeRaw === "full" ? "fullscreen" : sizeRaw;
    if (!["small", "medium", "fullscreen"].includes(size)) return;
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: "CHATBOT_WIDGET_SIZE", size }, "*");
      }
    } catch {}
  }, [botSettings?.window_size]);

  // Function to format message text with clickable links
  const formatMessageWithLinks = (text, links = []) => {
    if (!text) return '';
    if (!links || links.length === 0) {
      // Still try to detect URLs in text and make them clickable
      const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+)/gi;
      const parts = text.split(urlRegex);
      return parts.map((part, index) => {
        if (urlRegex.test(part)) {
          const url = part.startsWith('http') ? part : `https://${part}`;
          return (
            <a
              key={index}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="message-link"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }
        return <span key={index}>{part}</span>;
      });
    }

    // Replace URLs in text with clickable links
    let formattedText = text;
    links.forEach((url, index) => {
      const urlPattern = url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escape special regex chars
      const regex = new RegExp(urlPattern, 'gi');
      formattedText = formattedText.replace(regex, (match) => {
        return `__LINK_${index}__`;
      });
    });

    // Split by link placeholders and create React elements
    const parts = formattedText.split(/(__LINK_\d+__)/);
    return parts.map((part, index) => {
      const linkMatch = part.match(/__LINK_(\d+)__/);
      if (linkMatch) {
        const linkIndex = parseInt(linkMatch[1]);
        const url = links[linkIndex];
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noreferrer"
            className="message-link"
            onClick={(e) => e.stopPropagation()}
          >
            {url}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Auto-scroll function
  const scrollToBottom = () => {
    const messagesContainer = document.querySelector('.chatbot-messages');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  };

  // Initialize chatbot session timeout
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // Fetch suggested questions
  const fetchSuggestedQuestions = async () => {
    try {
      // Get bot_id from URL params, botSettings, or default to 'MAIN'
      const urlParams = new URLSearchParams(window.location.search);
      const botId = urlParams.get('bot_id') || botSettings?.bot_id || 'MAIN';
      
      const response = await fetchWidgetApiWithFallback(
        `repetitive-questions?active_only=true&bot_id=${encodeURIComponent(botId)}`
      );
      if (response.ok) {
        const result = await response.json();
        const suggestions = result.questions || [];
        setSuggestedQuestions(suggestions);
        return suggestions;
      }
    } catch (error) {
      console.error('Error fetching suggested questions:', error);
    }
    return [];
  };

  // Fetch bot settings and initialize messages
  const fetchBotSettings = async () => {
    try {
      // Get bot_id from URL params or default to 'MAIN'
      const urlParams = new URLSearchParams(window.location.search);
      const botId = urlParams.get('bot_id') || 'MAIN';
      console.log('[Chatbot] Fetching settings with bot_id:', botId, 'from URL:', window.location.search);

      // Try to load cached settings first for instant display
      const cacheKey = `chatbot_settings_${botId}`;
      const cachedSettings = localStorage.getItem(cacheKey);
      if (cachedSettings) {
        try {
          const parsed = JSON.parse(cachedSettings);
          const cacheAge = Date.now() - (parsed._cachedAt || 0);
          // Use cache if less than 5 minutes old
          if (cacheAge < 5 * 60 * 1000) {
            // Keep cache available, but do not render from cache first.
            // We want wallpaper/theme updates to reflect immediately from server values.
            console.log('[Chatbot] Cache present for bot_id:', botId, '- fetching fresh settings');
          }
        } catch (e) {
          console.warn('[Chatbot] Failed to parse cached settings:', e);
        }
      }

      // Fetch suggested questions first so they're available for the welcome message
      const suggestions = await fetchSuggestedQuestions();

      const response = await fetchWidgetApiWithFallback(
        `settings?bot_id=${encodeURIComponent(botId)}&_ts=${Date.now()}`
      );
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.settings) {
          const settings = {
            ...result.settings,
            bot_id: botId  // Ensure bot_id is always set from URL param
          };
          console.log('[Chatbot] Loaded settings for bot_id:', botId, 'bot_name:', settings.bot_name, 'Settings:', settings);
          
          // Cache settings for faster subsequent loads
          try {
            const settingsToCache = { ...settings, _cachedAt: Date.now() };
            localStorage.setItem(cacheKey, JSON.stringify(settingsToCache));
          } catch (e) {
            console.warn('[Chatbot] Failed to cache settings:', e);
          }
          
          setBotSettings(settings);

          // Restore prior session (messages + survey + escalation) for this bot
          let restored = false;
          try {
            // On browser refresh/hard-refresh, intentionally start from a clean chat state.
            const navEntries = (typeof performance !== "undefined" && performance.getEntriesByType)
              ? performance.getEntriesByType("navigation")
              : [];
            const isReload = Array.isArray(navEntries) && navEntries[0] && navEntries[0].type === "reload";
            // Only wipe stored chat on a top-level page refresh — not embed iframe reloads.
            if (isReload && !isEmbeddedWidget) {
              const keys = widgetStorageKeys(botId);
              localStorage.removeItem(keys.state);
              localStorage.removeItem(keys.session);
            }
            const raw = localStorage.getItem(widgetStorageKeys(botId).state);
            if (raw) {
              const st = JSON.parse(raw);
              if (
                st.v === WIDGET_PERSIST_VERSION &&
                Array.isArray(st.messages) &&
                st.messages.length > 0
              ) {
                if (st.conversationId) {
                  try {
                    localStorage.setItem(widgetStorageKeys(botId).session, st.conversationId);
                  } catch (_) {}
                  setConversationId(st.conversationId);
                }
                setMessages(st.messages);
                setSurveyCompleted(!!st.surveyCompleted);
                setSurveyStart(!!st.surveyStart);
                setSurveyResponses(Array.isArray(st.surveyResponses) ? st.surveyResponses : []);
                const idx =
                  typeof st.currentQuestionIndex === "number" ? st.currentQuestionIndex : 0;
                setCurrentQuestionIndex(idx);
                setHasSentFirstChatMessage(!!st.hasSentFirstChatMessage);
                setRedirectMode(!!st.redirectMode);
                setRedirectStep(typeof st.redirectStep === "number" ? st.redirectStep : 0);
                setRedirectData(
                  st.redirectData && typeof st.redirectData === "object"
                    ? st.redirectData
                    : { user_name: "", user_email: "", user_phone: "" }
                );
                setHasShownValidationError(false);
                setIsProcessingAnswer(false);

                if (st.surveyStart && !st.surveyCompleted) {
                  const sqRes = await fetchWidgetApiWithFallback(
                    `predefined-questions?active_only=true&bot_id=${encodeURIComponent(botId)}`
                  );
                  if (sqRes.ok) {
                    const sqData = await sqRes.json();
                    const active = (sqData.questions || []).filter((q) => q.isActive);
                    const sorted = active.sort((a, b) => a.orderIndex - b.orderIndex);
                    setSurveyQuestions(sorted);
                    if (sorted.length && idx >= sorted.length) {
                      setCurrentQuestionIndex(Math.max(0, sorted.length - 1));
                    }
                  }
                }
                restored = true;
              }
            }
          } catch (e) {
            console.warn("[Chatbot] Widget session restore failed:", e);
          }

          if (restored) {
            hydrationDoneRef.current = true;
            setSettingsLoaded(true);
            return;
          }

          // Fetch survey questions to check if we should even prompt for it
          const questions = await fetchSurveyQuestions(botId);
          const hasQuestions = questions && questions.length > 0;
          const shouldPromptSurvey = !!settings.survey_enabled && hasQuestions;

          let welcomeText = settings.welcome_message || "Hello! I'm Weta Nex. How can I help you today? 😊";
          let followUps = [];

          // Only show suggestions that have matching content in the knowledge base
          // No hardcoded fallbacks - only show questions that can actually be answered
          if (settings.survey_enabled && !hasQuestions) {
            followUps = suggestions.slice(0, 3).map(q => q.question || q.questionText);
          } else if (!settings.survey_enabled) {
            followUps = suggestions.slice(0, 3).map(q => q.question || q.questionText);
          }

          setMessages([
            {
              sender: "bot",
              text: welcomeText,
              surveyPrompt: shouldPromptSurvey,
              smartFollowUps: !shouldPromptSurvey ? followUps : []
            }
          ]);

          // If survey is disabled or empty, open chat input immediately
          if (!shouldPromptSurvey) {
            setSurveyCompleted(true);
            setSurveyStart(false);
          }

          hydrationDoneRef.current = true;
          // Mark settings as loaded to prevent flash of unstyled content
          setSettingsLoaded(true);
          return;
        }
      }
    } catch (error) {
      console.error('Error fetching bot settings:', error);
    }

    // Fallback if no settings are fetched
    const urlParams = new URLSearchParams(window.location.search);
    const botIdFromUrl = urlParams.get('bot_id');
    
    if (!botIdFromUrl || botIdFromUrl === 'MAIN') {
      console.warn('[Chatbot] No bot_id provided in URL. Using default "MAIN". To use a specific bot, add ?bot_id=YOUR_BOT_ID to the URL.');
    }
    
    const botIdFb = (new URLSearchParams(window.location.search).get("bot_id") || "MAIN");
    let restoredFb = false;
    try {
      const raw = localStorage.getItem(widgetStorageKeys(botIdFb).state);
      if (raw) {
        const st = JSON.parse(raw);
        if (
          st.v === WIDGET_PERSIST_VERSION &&
          Array.isArray(st.messages) &&
          st.messages.length > 0
        ) {
          if (st.conversationId) {
            try {
              localStorage.setItem(widgetStorageKeys(botIdFb).session, st.conversationId);
            } catch (_) {}
            setConversationId(st.conversationId);
          }
          setMessages(st.messages);
          setSurveyCompleted(!!st.surveyCompleted);
          setSurveyStart(!!st.surveyStart);
          setSurveyResponses(Array.isArray(st.surveyResponses) ? st.surveyResponses : []);
          setCurrentQuestionIndex(
            typeof st.currentQuestionIndex === "number" ? st.currentQuestionIndex : 0
          );
          setHasSentFirstChatMessage(!!st.hasSentFirstChatMessage);
          setRedirectMode(!!st.redirectMode);
          setRedirectStep(typeof st.redirectStep === "number" ? st.redirectStep : 0);
          setRedirectData(
            st.redirectData && typeof st.redirectData === "object"
              ? st.redirectData
              : { user_name: "", user_email: "", user_phone: "" }
          );
          restoredFb = true;
        }
      }
    } catch (_) {}

    if (!restoredFb) {
      setMessages([
        {
          sender: "bot",
          text: "Hello! I'm Weta Nex. How can I help you today? 😊",
          surveyPrompt: false,
          smartFollowUps: [],
        },
      ]);
      setSurveyCompleted(true);
      setSurveyStart(false);
    }

    hydrationDoneRef.current = true;
    // Mark settings as loaded even in fallback case
    setSettingsLoaded(true);
  };

  const parsePopupTriggerKeywords = (settings) => {
    const raw = settings?.popup_trigger_words || '';
    if (!raw.trim()) return [];
    return raw.split(',').map((k) => k.trim()).filter(Boolean);
  };

  const getPopupVersionValue = (settings) =>
    JSON.stringify({
      t: settings?.popup_type,
      m: settings?.popup_message,
      lt: settings?.popup_link_text,
      lu: settings?.popup_link_url,
      kw: settings?.popup_trigger_words,
    });

  const maybeShowKeywordPopup = (userText, settings) => {
    const noMatch = { matched: false, shown: false };
    if (!settings?.popup_enabled) return noMatch;

    const keywords = parsePopupTriggerKeywords(settings);
    if (!keywords.length) return noMatch;
    if (!messageMatchesKeywords(userText, keywords)) return noMatch;

    const botKey = settings?.bot_id || 'MAIN';
    const versionKey = `chatbot_popup_version_${botKey}`;
    const versionValue = getPopupVersionValue(settings);
    const prevVersion = localStorage.getItem(versionKey);
    let skipForSession = hasShownPopupInSession;
    if (prevVersion !== versionValue) {
      localStorage.setItem(versionKey, versionValue);
      popupVersionRef.current = versionValue;
      skipForSession = false;
    }

    if (skipForSession) {
      console.log('[POPUP] Already shown in this session, skipping keyword popup');
      return { matched: true, shown: false };
    }

    console.log('[POPUP] Keyword match — showing popup');
    appendPopup(settings);
    setHasShownPopupInSession(true);
    return { matched: true, shown: true };
  };

  const appendPopup = (settings) => {
    const text = settings.popup_message || '';
    const linkUrl = settings.popup_type === 'link' ? (settings.popup_link_url || '') : '';
    const linkText = settings.popup_type === 'link' ? (settings.popup_link_text || 'Open') : '';
    if (!text && !linkUrl) return;
    setMessages(prev => [...prev, {
      sender: 'bot',
      text,
      linkUrl,
      linkText,
      isPopup: true
    }]);
  };

  // Initialize Speech Recognition
  useEffect(() => {
    // Check if browser supports speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = languages[selectedLanguage]?.code || 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        // Update input with final transcript
        if (finalTranscript) {
          setInput(prev => prev + finalTranscript.trim());
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'no-speech') {
          // User didn't speak, just stop listening
        } else if (event.error === 'not-allowed' || event.error === 'permission-denied') {
          const inEmbed = typeof window !== 'undefined' && window.self !== window.top;
          const msg = inEmbed
            ? 'Microphone is often blocked when the chat is embedded on another site. Try opening the chat in a new tab, or ask the site owner to allow microphone for this embed.'
            : 'Microphone permission denied. Please enable microphone access in your browser settings.';
          alert(msg);
        }
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [selectedLanguage]);

  // Initialize Speech Synthesis
  useEffect(() => {
    if ('speechSynthesis' in window) {
      synthRef.current = window.speechSynthesis;
      // Load voices (some browsers need this)
      if (synthRef.current.getVoices().length === 0) {
        synthRef.current.onvoiceschanged = () => {
          // Voices loaded
        };
      }
    }
    return () => {
      // Cancel any ongoing speech when component unmounts
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  // Function to speak text
  const speakText = (text) => {
    if (!synthRef.current || !voiceOutputEnabled) return;

    // Clean text - remove URLs, special characters that don't read well
    let cleanText = text
      .replace(/https?:\/\/[^\s]+/g, '') // Remove URLs
      .replace(/\[listening\.\.\.\]/g, '') // Remove listening indicator
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    // Remove emojis and special unicode characters for better speech
    cleanText = cleanText.replace(/[\u{1F600}-\u{1F64F}]/gu, '') // Emoticons
      .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') // Misc symbols
      .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') // Transport
      .replace(/[\u{2600}-\u{26FF}]/gu, '') // Misc symbols
      .replace(/[\u{2700}-\u{27BF}]/gu, ''); // Dingbats

    if (!cleanText || cleanText.length < 2) return;

    // Cancel any ongoing speech
    synthRef.current.cancel();

    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 0.95; // Slightly slower for clarity
    utterance.pitch = 1.0; // Normal pitch
    utterance.volume = 1.0; // Full volume

    // Get voices and select a good one
    const voices = synthRef.current.getVoices();
    if (voices.length > 0) {
      const targetLang = languages[selectedLanguage]?.code.split('-')[0] || 'en';
      const preferredVoice = voices.find(voice =>
        voice.lang.includes(targetLang) && (voice.name.includes('Female') || voice.name.includes('Karen') || voice.name.includes('Samantha'))
      ) || voices.find(voice => voice.lang.includes(targetLang)) || voices[0];

      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
    }

    synthRef.current.speak(utterance);
  };

  // Stop speaking immediately when voice output is disabled
  useEffect(() => {
    if (!voiceOutputEnabled && synthRef.current) {
      synthRef.current.cancel();
    }
  }, [voiceOutputEnabled]);

  // Watch for new bot messages and speak them
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.sender === 'bot' && lastMessage.text) {
      // Create a unique key for this message (text + index to handle duplicates)
      const messageKey = `${lastMessage.text}-${messages.length - 1}`;

      // Only speak if voice output is enabled, not already spoken, and not a welcome message with survey prompt
      if (voiceOutputEnabled &&
        !spokenMessagesRef.current.has(messageKey) &&
        !lastMessage.surveyPrompt &&
        !lastMessage.questionType &&
        !lastMessage.isPopup) {

        // Mark as spoken
        spokenMessagesRef.current.add(messageKey);

        // Small delay to ensure message is displayed first
        setTimeout(() => {
          speakText(lastMessage.text);
        }, 300);
      }
    }
  }, [messages, voiceOutputEnabled]);

  // Ensure session id exists (embed / strict mode)
  useEffect(() => {
    if (typeof window === "undefined" || conversationId) return;
    const botId = new URLSearchParams(window.location.search).get("bot_id") || "MAIN";
    setConversationId(readOrCreateWidgetSessionId(botId));
  }, [conversationId]);

  // Initialize component
  useEffect(() => {
    fetchBotSettings();
    // Prepare a simple audio context for beeps
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      setAudioCtx(ctx);
    } catch (e) {
      // ignore if not supported
    }
  }, []);

  // Persist widget state across page loads (same browser + bot_id)
  useEffect(() => {
    if (!settingsLoaded || !hydrationDoneRef.current) return;
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(flushWidgetStateNow, 250);
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        persistTimerRef.current = null;
      }
    };
  }, [
    messages,
    surveyCompleted,
    surveyStart,
    surveyResponses,
    currentQuestionIndex,
    hasSentFirstChatMessage,
    redirectMode,
    redirectStep,
    redirectData,
    conversationId,
    settingsLoaded,
    botSettings?.bot_id,
  ]);

  // Flush immediately when the tab/iframe is hidden or unloaded (close widget, navigate away).
  useEffect(() => {
    const onHide = () => flushWidgetStateNow();
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });
    return () => {
      window.removeEventListener("pagehide", onHide);
      flushWidgetStateNow();
    };
  }, []);

  // Host embed script hides iframe (owl toggle / overlay) — persist before panel is hidden.
  useEffect(() => {
    if (!isEmbeddedWidget) return;
    const onHostMessage = (event) => {
      if (event.data?.type === "CHATBOT_WIDGET_HIDE") {
        flushWidgetStateNow();
      }
    };
    window.addEventListener("message", onHostMessage);
    return () => window.removeEventListener("message", onHostMessage);
  }, [isEmbeddedWidget]);

  // Handle session expiration: append message and disable input
  useEffect(() => {
    if (sessionExpired && !hasAppendedExpiry) {
      setMessages(prev => [...prev, {
        sender: "bot",
        text: expiredMessage || "This chat session has ended due to inactivity. Please start a new conversation."
      }]);
      setHasAppendedExpiry(true);
    }
  }, [sessionExpired, expiredMessage, hasAppendedExpiry]);

  // Auto-scroll when new messages are added
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Prevent global Enter key during survey
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (surveyStart && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    if (surveyStart) {
      document.addEventListener('keydown', handleGlobalKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [surveyStart]);

  // Fetch survey questions
  const fetchSurveyQuestions = async (overrideBotId) => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const botId = overrideBotId || urlParams.get('bot_id') || botSettings?.bot_id || 'MAIN';
      
      const response = await fetchWidgetApiWithFallback(
        `predefined-questions?active_only=true&bot_id=${encodeURIComponent(botId)}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const activeQuestions = (data.questions || []).filter(q => q.isActive);
      const sortedQuestions = activeQuestions.sort((a, b) => a.orderIndex - b.orderIndex);
      setSurveyQuestions(sortedQuestions);
      return sortedQuestions;
    } catch (error) {
      console.error('Error fetching survey questions:', error);
      return [];
    }
  };

  // Auto-resize textarea
  const handleInputChange = (e) => {
    setInput(e.target.value);

    // Reset textarea height to auto-calculate
    e.target.style.height = 'auto';
    // Show suggestions only if input is empty AND user hasn't sent any main chat messages yet
    if (e.target.value.trim() === '' && suggestedQuestions.length > 0 && !hasSentFirstChatMessage) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleInputFocus = () => {
    if (input.trim() === '' && suggestedQuestions.length > 0 && !hasSentFirstChatMessage) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow click on suggestion
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  // Voice-to-text handlers
  const startListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    if (isListening) {
      stopListening();
      return;
    }

    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      setIsListening(false);
      const inEmbed = typeof window !== 'undefined' && window.self !== window.top;
      const msg = inEmbed
        ? 'Voice input is not available when the chat is embedded on another site. Open the chat in a new tab to use the microphone.'
        : (error && error.message) || 'Could not start microphone. Please check browser permissions.';
      alert(msg);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const handleSuggestionClick = (question) => {
    const questionText = question.question || question.questionText || '';
    setInput(questionText);
    setShowSuggestions(false);
    // Focus the input and update textarea height after setting value
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // Update textarea height to match content
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 200) + 'px';
      }
    }, 0);
  };

  const handleCreateTicket = async (finalData) => {
    setIsTyping(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: conversationId,
          user_name: finalData.user_name,
          user_email: finalData.user_email,
          user_phone: finalData.user_phone,
          chat_history: messages,
          bot_id: botSettings?.bot_id
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessages(prev => [...prev, {
          sender: "bot",
          text: `✅ HIGH PRIORITY ESCALATION SUCCESSFUL.\n\nTicket #${result.id.slice(-6).toUpperCase()} has been raised. A subject matter expert will contact you at ${finalData.user_email} within 24 hours.`
        }]);
        setRedirectStep(4);
      } else {
        throw new Error("Failed to create ticket");
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      setMessages(prev => [...prev, {
        sender: "bot",
        text: "I encountered an error raising the ticket, but I have logged your request. Our team will look into it."
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleRedirectInput = () => {
    const userInput = input.trim();
    const currentData = { ...redirectData };
    let nextStep = redirectStep;

    if (redirectStep === 1) { // Collected Name
      currentData.user_name = userInput;
      setRedirectData(currentData);
      setInput("");

      setTimeout(() => {
        setMessages(prev => [...prev, { sender: "bot", text: `Got it, ${userInput.split(' ')[0]}. What is your business email? 📧` }]);
        setRedirectStep(2);
        scrollToBottom();
      }, 800);

    } else if (redirectStep === 2) { // Collected Email
      if (!userInput.includes('@') || !userInput.includes('.')) {
        alert("Please enter a valid email address");
        return;
      }
      currentData.user_email = userInput;
      setRedirectData(currentData);
      setInput("");

      handleCreateTicket(currentData);
    }
  };

  const sendMessage = async () => {
    if (sessionExpired) return;
    if (!conversationId) return;
    if (!input.trim()) return;

    // Track activity for session timeout
    trackActivity();

    // Mark that the first chat message has been sent
    if (!hasSentFirstChatMessage) {
      setHasSentFirstChatMessage(true);
    }

    // Only allow messages after survey is completed
    if (!surveyCompleted || surveyStart || isProcessingAnswer) {
      return; // Don't send messages until survey is completed
    }

    const userMessage = { sender: "user", text: input };
    setMessages((prev) => [...prev, userMessage]);
    const messageText = input; // Store the message before clearing input
    const popupResult = maybeShowKeywordPopup(messageText, botSettings);
    setInput("");
    setIsTyping(true);

    const textarea = document.querySelector('.chatbot-input textarea');
    if (textarea) {
      textarea.style.height = '60px';
    }

    if (redirectMode && redirectStep < 4) {
      handleRedirectInput();
      return;
    }

    try {
      // Get bot_id from settings or URL params
      const urlParams = new URLSearchParams(window.location.search);
      const botId = botSettings?.bot_id || urlParams.get('bot_id') || 'MAIN';
      const visitorUserId = readOrCreateVisitorId(botId);
      
      // Chat API: prefer /chatbot/api/widget/chat so nginx ^~ /chatbot/api/ proxies POST.
      // /chatbot/chat needs a separate "location = /chatbot/chat" proxy; without it, SPA static
      // location returns 405 for POST (common production misconfig).
      const chatCandidates =
        process.env.NODE_ENV === 'production'
          ? ['/chatbot/api/widget/chat', '/chatbot/chat']
          : [`${API_BASE_URL}/api/widget/chat`, `${API_BASE_URL}/chat`];

      let response = null;
      let lastStatus = 0;
      for (const url of chatCandidates) {
        const r = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': visitorUserId,
          },
          body: JSON.stringify({
            message: messageText,
            user_id: visitorUserId,
            user_name: 'Visitor',
            session_id: conversationId,
            bot_id: botId,
            popup_keyword_handled: !!popupResult.shown,
          }),
        });
        if (r.ok) {
          response = r;
          break;
        }
        lastStatus = r.status;
      }

      if (!response) {
        throw new Error(`HTTP error! status: ${lastStatus || 'unknown'}`);
      }

      const data = await response.json();

      if (data.reply === "POPUP_KEYWORD_TRIGGER") {
        if (data.conversation_id) setConversationId(data.conversation_id);
        return;
      }

      if (data.reply === "RED_REDIRECT_TRIGGER" || data.reply === "RED_ESCALATION_TRIGGER") {
        setRedirectMode(true);
        if (data.conversation_id) setConversationId(data.conversation_id);
        const isUnknownEscalation = data.reply === "RED_ESCALATION_TRIGGER";

        // Try to pre-fill from survey if available
        let prefilledData = { ...redirectData };
        surveyResponses.forEach(resp => {
          const qText = resp.questionText.toLowerCase();
          if (qText.includes('name')) prefilledData.user_name = resp.userAnswer;
          if (qText.includes('email')) prefilledData.user_email = resp.userAnswer;
        });
        setRedirectData(prefilledData);

        const botReply = {
          sender: "bot",
          text: isUnknownEscalation
            ? "I'm sorry, I don't have that specific information in my knowledge base at the moment. 😔\n\nTo ensure you get the right answer, I'd like to escalate this to our support team. Would you please provide your details so they can get back to you?"
            : "Thanks for your interest! For detailed information on our pricing and custom solutions, I'll connect you with one of our specialized representatives. \n\nPlease provide your details below so they can share the pricing details with you.",
          smartFollowUps: [] // Explicitly clear follow-ups for escalation messages
        };

        setMessages(prev => [...prev, botReply]);

        // Determine the next step based on what we already have
        setTimeout(() => {
          if (!prefilledData.user_name) {
            setMessages(prev => [...prev, { sender: "bot", text: "What is your full name? 👤" }]);
            setRedirectStep(1);
          } else if (!prefilledData.user_email) {
            const nextText = isUnknownEscalation
              ? `Thanks ${prefilledData.user_name}. What is your email address so we can reach out? 📧`
              : `Thanks ${prefilledData.user_name}. What is your business email? 📧`;
            setMessages(prev => [...prev, { sender: "bot", text: nextText }]);
            setRedirectStep(2);
          } else {
            // Already have all info, just create the ticket
            handleCreateTicket(prefilledData);
          }
          scrollToBottom();
        }, 1500);

        return;
      }

      const botReply = {
        sender: "bot",
        text: data.reply || "I'm sorry, I couldn't process your message. Please try again.",
        Link: data.Link || null,
        smartFollowUps: sanitizeSmartFollowUps(data.smart_follow_ups || [], data.reply)
      };

      setMessages((prev) => {
        const next = [...prev, botReply];
        playBeep();
        return next;
      });

      // Note: speakText is called in useEffect watching messages array

      // Auto-scroll after bot response
      setTimeout(() => {
        scrollToBottom();
      }, 100);

      // Update conversation ID if provided
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      const botReply = {
        sender: "bot",
        text: "I'm sorry, I'm having trouble connecting to the server. Please try again later."
      };

      setMessages((prev) => {
        const next = [...prev, botReply];
        playBeep();
        return next;
      });

      // Note: speakText is called in useEffect watching messages array

      // Auto-scroll after error response
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (sessionExpired) {
        return;
      }

      // Only process Enter key after survey is completed
      if (!surveyCompleted || surveyStart) {
        return;
      }

      sendMessage();
    }
  };

  const startSurvey = async () => {
    const questions = await fetchSurveyQuestions();
    if (questions.length === 0) {
      setMessages(prev => [...prev, {
        sender: "bot",
        text: "I'm ready to help! 🚀 Since we don't have any specific survey questions today, feel free to ask me anything directly. What would you like to know?",
        smartFollowUps: suggestedQuestions.slice(0, 3).map(q => q.question || q.questionText)
      }]);
      setSurveyCompleted(true);
      setSurveyStart(false);
      return;
    }

    setSurveyStart(true);
    setCurrentQuestionIndex(0);
    setHasShownValidationError(false); // Reset validation state
    setCurrentTextValue(''); // Clear any existing text
    setIsProcessingAnswer(false); // Reset processing state

    // Add first question as a chat message
    setTimeout(() => {
      const firstQuestion = questions[0];
      setMessages(prev => [...prev, {
        sender: "bot",
        text: firstQuestion.questionText,
        questionType: firstQuestion.questionType,
        options: firstQuestion.options,
        questionId: firstQuestion.id,
        isRequired: firstQuestion.isRequired,
        questionNumber: 1,
        totalQuestions: questions.length
      }]);

      // Auto-scroll to the first question
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }, 1000);
  };

  const handleSurveyAnswer = async (answer, selectedOptions, questionId) => {
    // Prevent duplicate processing
    if (isProcessingAnswer) {
      return;
    }

    // Additional check - if answer is empty and no selected options, don't proceed
    if (!answer && (!selectedOptions || selectedOptions.length === 0)) {
      return;
    }

    setIsProcessingAnswer(true);
    const currentQuestion = surveyQuestions[currentQuestionIndex];

    // Validate response for required questions
    const isValidResponse = validateSurveyResponse(currentQuestion.questionType, answer, selectedOptions);

    if (!isValidResponse) {
      setIsProcessingAnswer(false);
      // Show validation message only once per question attempt
      if (!hasShownValidationError) {
        setMessages(prev => [...prev, {
          sender: "bot",
          text: "Please provide a valid response before continuing. This question is required. ✅"
        }]);
        setHasShownValidationError(true);

        // Auto-scroll to validation message
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
      return; // Don't proceed without valid response
    }

    // Clear validation error state when valid response is provided
    setHasShownValidationError(false);
    setCurrentTextValue(''); // Clear text input

    // Add user response as a chat message
    let responseText = '';
    if (currentQuestion.questionType.includes('text')) {
      responseText = answer;
    } else if (currentQuestion.questionType === 'rating') {
      responseText = `${answer} out of 5 stars`;
    } else if (currentQuestion.questionType === 'single_choice') {
      responseText = answer;
    } else if (currentQuestion.questionType === 'multiple_choice') {
      responseText = selectedOptions.join(', ');
    }

    setMessages(prev => [...prev, {
      sender: "user",
      text: responseText
    }]);

    // Store the response
    const response = {
      questionId: questionId,
      userAnswer: answer,
      selectedOptions: selectedOptions,
      questionText: currentQuestion.questionText // Include question text for conversation history
    };
    setSurveyResponses(prev => [...prev, response]);

    // Move to next question or submit
    if (currentQuestionIndex < surveyQuestions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestion = surveyQuestions[nextIndex];
      setTimeout(() => {
        setCurrentQuestionIndex(nextIndex);
        setHasShownValidationError(false); // Reset validation state for next question
        setCurrentTextValue(''); // Clear text input for next question
        // Add next question only once (avoid duplicate if user double-clicks Submit)
        setMessages(prev => {
          const lastMsg = prev[prev.length - 1];
          const alreadyAdded = lastMsg && lastMsg.sender === 'bot' && lastMsg.questionId === nextQuestion?.id;
          if (alreadyAdded) return prev;
          return [...prev, {
            sender: "bot",
            text: nextQuestion.questionText,
            questionType: nextQuestion.questionType,
            options: nextQuestion.options,
            questionId: nextQuestion.id,
            isRequired: nextQuestion.isRequired,
            questionNumber: nextIndex + 1,
            totalQuestions: surveyQuestions.length
          }];
        });

        // Auto-scroll to the new question
        setTimeout(() => {
          scrollToBottom();
        }, 100);
        setIsProcessingAnswer(false);
      }, 500);
    } else {
      // Survey completed
      await submitSurveyResponses([...surveyResponses, response]);
      setSurveyCompleted(true);
      setSurveyStart(false);

      // Auto-scroll after survey completion
      setTimeout(() => {
        scrollToBottom();
      }, 100);
      setIsProcessingAnswer(false);
    }
  };

  const isCommonDefaultValue = (value) => {
    const lowerValue = value.toLowerCase().trim();
    const commonDefaults = [
      'technical support',
      'default',
      'test',
      'example',
      '5',
      'good',
      'ok',
      'fine',
      'thanks',
      'bye'
    ];
    return commonDefaults.includes(lowerValue) || lowerValue.length < 2;
  };

  const validateSurveyResponse = (questionType, answer, selectedOptions) => {
    // Every question is now considered mandatory
    if (questionType.includes('text')) {
      // Text input must have some content (not empty)
      return answer && answer.trim().length > 0;
    } else if (questionType === 'rating') {
      // Rating must be a valid selection between 1 and 5
      const rating = parseInt(answer);
      return rating >= 1 && rating <= 5 && !isNaN(rating);
    } else if (questionType === 'single_choice') {
      // Single choice must have a selected option
      return answer && answer.trim().length > 0;
    } else if (questionType === 'multiple_choice') {
      // Multiple choice must have at least one option selected
      return selectedOptions && selectedOptions.length > 0;
    }
    return false; // Default to invalid
  };

  const submitSurveyResponses = async (responses) => {
    try {
      const surveyBotId = botSettings?.bot_id || new URLSearchParams(window.location.search).get('bot_id') || 'MAIN';
      const visitorUserId = readOrCreateVisitorId(surveyBotId);
      // Generate a user name from responses or use default
      let userName = 'Visitor';
      const nameQuestion = responses.find(r =>
        r.questionText && r.questionText.toLowerCase().includes('name')
      );
      if (nameQuestion && nameQuestion.userAnswer) {
        userName = nameQuestion.userAnswer;
      } else {
        // Try to generate a user-friendly name from email if available
        const emailQuestion = responses.find(r =>
          r.questionText && r.questionText.toLowerCase().includes('email')
        );
        if (emailQuestion && emailQuestion.userAnswer) {
          const email = emailQuestion.userAnswer;
          const emailPrefix = email.split('@')[0];
          userName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
        }
      }

      const submitResponse = await fetch(`${API_BASE_URL}/api/user-survey`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': visitorUserId,
        },
        body: JSON.stringify({
          conversationId: conversationId,
          responses: responses,
          user_name: userName,
          user_id: visitorUserId
        }),
      });

      if (!submitResponse.ok) {
        const errorData = await submitResponse.json();
        throw new Error(`Failed to submit survey: ${errorData.message || submitResponse.statusText}`);
      }

      // Add completion message
      setTimeout(() => {
        setMessages(prev => [...prev, {
          sender: "bot",
          text: "Thank you for completing the survey! Your feedback helps us improve our service. 🎉"
        }]);

        // Auto-scroll to completion message
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }, 500);
    } catch (error) {
      console.error("Error submitting survey:", error);
      setMessages(prev => [...prev, {
        sender: "bot",
        text: "Thank you for your responses! (Note: There was an issue saving your feedback, but we've noted your input.)"
      }]);

      // Auto-scroll to error message
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  };

  const handleTextSubmit = (inputValue, questionId) => {
    const trimmedValue = inputValue.trim();

    // Find the current question to check if it's an email field
    const currentQuestion = surveyQuestions[currentQuestionIndex];
    const isEmailField = currentQuestion &&
      currentQuestion.questionText &&
      currentQuestion.questionText.toLowerCase().includes('email');

    // Validate the input
    if (trimmedValue.length === 0) {
      // Show validation message for empty input
      if (!hasShownValidationError) {
        setMessages(prev => [...prev, {
          sender: "bot",
          text: "Please provide an answer. This question is required. ✅"
        }]);
        setHasShownValidationError(true);

        // Auto-scroll to validation message
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
      return;
    }

    // Validate email format if it's an email field
    if (isEmailField) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedValue)) {
        // Show email validation error
        if (!hasShownValidationError) {
          setMessages(prev => [...prev, {
            sender: "bot",
            text: "Please provide a valid email address (e.g., user@example.com). ✉️"
          }]);
          setHasShownValidationError(true);

          // Auto-scroll to validation message
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }
        return;
      }
    }

    // If all validations pass, proceed with the answer
    handleSurveyAnswer(trimmedValue, [], questionId);
    setCurrentTextValue('');
  };

  const renderQuestionInputs = (message) => {
    if (!message.questionType) return null;

    switch (message.questionType) {
      case 'rating':
        return (
          <div className="chat-rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                className="star-btn"
                type="button"
                disabled={isProcessingAnswer}
                onClick={(e) => {
                  e.preventDefault();
                  handleSurveyAnswer(star.toString(), [star.toString()], message.questionId);
                }}
                onKeyDown={(e) => {
                  // Prevent Enter key from triggering submission on star buttons
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSurveyAnswer(star.toString(), [star.toString()], message.questionId);
                  }
                }}
              >
                ⭐
              </button>
            ))}
          </div>
        );

      case 'single_choice':
        return (
          <div className="chat-options">
            {message.options.map((option, index) => (
              <button
                key={index}
                className="option-btn"
                type="button"
                disabled={isProcessingAnswer}
                onClick={(e) => {
                  e.preventDefault();
                  handleSurveyAnswer(option, [option], message.questionId);
                }}
                onKeyDown={(e) => {
                  // Prevent Enter key from triggering submission on visible options
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSurveyAnswer(option, [option], message.questionId);
                  }
                }}
              >
                {option}
              </button>
            ))}
          </div>
        );

      case 'multiple_choice':
        return (
          <div className="multiple-choice-container">
            {message.options.map((option, index) => (
              <label key={index} className="multiple-choice-option">
                <input
                  type="checkbox"
                  onChange={() => {
                    // Handle checkbox change - this will be managed by submit button
                  }}
                />
                <span className="option-text">{option}</span>
              </label>
            ))}
            <button
              className="submit-multiple-btn"
              type="button"
              disabled={isProcessingAnswer}
              onClick={(e) => {
                e.preventDefault();
                const checkboxes = document.querySelectorAll(`input[type="checkbox"]`);
                const selectedOptions = [];
                checkboxes.forEach((checkbox, index) => {
                  if (checkbox.checked) {
                    selectedOptions.push(message.options[index]);
                  }
                });
                if (selectedOptions.length > 0) {
                  handleSurveyAnswer(selectedOptions.join(', '), selectedOptions, message.questionId);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  // Same logic as onClick
                  const checkboxes = document.querySelectorAll(`input[type="checkbox"]`);
                  const selectedOptions = [];
                  checkboxes.forEach((checkbox, index) => {
                    if (checkbox.checked) {
                      selectedOptions.push(message.options[index]);
                    }
                  });
                  if (selectedOptions.length > 0) {
                    handleSurveyAnswer(selectedOptions.join(', '), selectedOptions, message.questionId);
                  }
                }
              }}
            >
              Submit Selection
            </button>
          </div>
        );

      case 'text_single':
      case 'text_multiline':
        return (
          <div className="chat-input-container">
            {message.questionType === 'text_single' ? (
              <>
                <input
                  type="text"
                  placeholder="Type your answer..."
                  className="chat-text-input"
                  value={currentTextValue}
                  onChange={(e) => {
                    setCurrentTextValue(e.target.value);
                    // Reset validation error when user starts typing
                    if (hasShownValidationError) {
                      setHasShownValidationError(false);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleTextSubmit(currentTextValue, message.questionId);
                    }
                  }}
                />
                <button
                  className="submit-text-btn"
                  type="button"
                  disabled={isProcessingAnswer}
                  onClick={(e) => {
                    e.preventDefault();
                    handleTextSubmit(currentTextValue, message.questionId);
                  }}
                >
                  Submit
                </button>
              </>
            ) : (
              <>
                <textarea
                  placeholder="Type your detailed answer..."
                  className="chat-textarea"
                  rows="3"
                  value={currentTextValue}
                  onChange={(e) => {
                    setCurrentTextValue(e.target.value);
                    // Reset validation error when user starts typing
                    if (hasShownValidationError) {
                      setHasShownValidationError(false);
                    }
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.shiftKey) {
                      // Allow Shift+Enter for new lines
                      return;
                    } else if (e.key === 'Enter') {
                      e.preventDefault();
                      handleTextSubmit(currentTextValue, message.questionId);
                    }
                  }}
                />
                <button
                  className="submit-text-btn"
                  type="button"
                  disabled={isProcessingAnswer}
                  onClick={(e) => {
                    e.preventDefault();
                    handleTextSubmit(currentTextValue, message.questionId);
                  }}
                >
                  Submit
                </button>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const themeStyle = {
    '--chat-primary': botSettings.theme_primary_color || '#2563eb',
    '--chat-header-bg': botSettings.theme_header_bg || '#2563eb',
    '--chat-header-text': botSettings.theme_header_text || '#ffffff',
    '--chat-background': botSettings.theme_background || '#f9fafb',
    '--chat-user-bubble-bg': botSettings.theme_user_bubble_bg || '#f8fafc',
    '--chat-bot-bubble-bg': botSettings.theme_bot_bubble_bg || '#ffffff',
    '--chat-border': botSettings.theme_border_color || '#e5e7eb',
    '--chat-text-color': botSettings.theme_text_color || '#2d3748',
    '--chat-wallpaper-image': botSettings.theme_wallpaper_url ? `url("${String(botSettings.theme_wallpaper_url).replace(/"/g, '\\"')}")` : 'none',
    '--chat-wallpaper-size': botSettings.theme_wallpaper_mode === 'contain' ? 'contain' : 'cover',
    '--chat-font': botSettings.theme_font_family || 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
    '--chat-gradient-enabled': botSettings.theme_gradient_enabled ? '1' : '0',
    '--chat-secondary': botSettings.theme_secondary_color || '#764ba2',
    '--chat-accent': botSettings.theme_accent_color || '#f093fb',
    '--chat-user-gradient-start': botSettings.theme_user_gradient_start || '#4facfe',
    '--chat-user-gradient-end': botSettings.theme_user_gradient_end || '#00f2fe',
    '--chat-bot-gradient-start': botSettings.theme_bot_gradient_start || '#667eea',
    '--chat-bot-gradient-end': botSettings.theme_bot_gradient_end || '#764ba2',
    '--chat-header-gradient-start': botSettings.theme_header_gradient_start || '#667eea',
    '--chat-header-gradient-mid': botSettings.theme_header_gradient_mid || '#764ba2',
    '--chat-header-gradient-end': botSettings.theme_header_gradient_end || '#f093fb'
  };

  const playBeep = () => {
    if (!audioCtx || !botSettings.sound_enabled) return;
    try {
      const duration = 0.08;
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(0.0001, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.08, audioCtx.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + duration);
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + duration);
    } catch (e) {
      // ignore
    }
  };

  // Don't render until settings are loaded to prevent flash of unstyled content
  if (!settingsLoaded) {
    return (
      <div
        className="chatbot-container"
        style={{ visibility: 'hidden', height: '100%' }}
      />
    );
  }

  const showChatIcon = isChatIconEnabled(botSettings);
  const headerIconRaw = normalizeChatIcon(botSettings.chat_icon, "💬");
  const botMessageIconRaw = normalizeChatIcon(botSettings.chat_icon, "💬");

  return (
    <div
      className={`chatbot-container${isEmbeddedWidget ? " chatbot-embed" : ""}`}
      style={themeStyle}
      data-gradient-enabled={botSettings.theme_gradient_enabled ? "true" : "false"}
    >
      {/* Header */}
      <div className="chatbot-header">
        <div className={`title${showChatIcon ? "" : " title--no-icon"}`}>
          {showChatIcon && (
            <span className="icon" aria-hidden>
              {isChatIconImageUrl(headerIconRaw) ? (
                <img
                  src={headerIconRaw}
                  alt=""
                  className="chatbot-header-owl-icon"
                  loading="eager"
                  decoding="async"
                />
              ) : (
                <span className="chatbot-header-icon-emoji">{headerIconRaw}</span>
              )}
            </span>
          )}
          <span>{botSettings.bot_name}</span>
        </div>
        <div className="header-actions">
          <select
            className="language-selector"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            title={`Select language - ${languages[selectedLanguage]?.name || 'English'}`}
            aria-label="Select language"
          >
            {Object.entries(languages).map(([code, lang]) => (
              <option key={code} value={code}>
                {code.toUpperCase()}
              </option>
            ))}
          </select>
          <button
            className={`voice-toggle-btn ${voiceOutputEnabled ? 'enabled' : ''}`}
            onClick={() => setVoiceOutputEnabled(!voiceOutputEnabled)}
            title={voiceOutputEnabled ? 'Disable voice output' : 'Enable voice output'}
            aria-label={voiceOutputEnabled ? 'Disable voice output' : 'Enable voice output'}
          >
            {voiceOutputEnabled ? '🔊' : '🔇'}
          </button>
          {onClose && (
            <button
              className="chatbot-close-btn"
              onClick={handleCloseClick}
              aria-label="Close chatbot"
            >
              ✖
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="chatbot-messages">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`message ${msg.sender}${msg.sender === "bot" && !showChatIcon && !msg.isPopup ? " message--no-bot-icon" : ""}${msg.isPopup ? " message--popup" : ""}`}
          >
            {(msg.sender === "user" || (showChatIcon && !msg.isPopup)) && (
            <div className="message-icon">
              {msg.sender === "bot" ? (
                isChatIconImageUrl(botMessageIconRaw) ? (
                  <img
                    src={botMessageIconRaw}
                    alt=""
                    className="message-avatar-img"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="message-avatar-emoji">{botMessageIconRaw}</span>
                )
              ) : (
                "👤"
              )}
            </div>
            )}
            <div className={`message-bubble${msg.isPopup ? " popup-message-bubble" : ""}`}>
              <div className="message-text">{msg.text}</div>
              {msg.Link && (
                <div className="message-link-container">
                  <div className="message-link-label">by using the below link:</div>
                  {Array.isArray(msg.Link) ? (
                    msg.Link.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="message-link-btn"
                      >
                        {url}
                      </a>
                    ))
                  ) : (
                    <a
                      href={msg.Link}
                      target="_blank"
                      rel="noreferrer"
                      className="message-link-btn"
                    >
                      {msg.Link}
                    </a>
                  )}
                </div>
              )}
              {msg.linkUrl && (
                <div className="message-actions">
                  <a
                    href={msg.linkUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="popup-link-btn"
                  >
                    {msg.linkText || 'Open'}
                  </a>
                </div>
              )}
              {msg.questionType && (
                <div className="question-indicator">
                  <small className="required-text">Required Question - Please answer to continue</small>
                </div>
              )}

              {msg.surveyPrompt && (
                <div className="survey-actions">
                  <button className="survey-yes-btn" onClick={startSurvey}>
                    🚀 Let's get started
                  </button>
                </div>
              )}

              {msg.questionType && renderQuestionInputs(msg)}
            </div>
          </div>
        ))}

        {isTyping && <div className="typing">Bot is typing</div>}
      </div>

      {/* Input - Hide until survey is completed */}
      {surveyCompleted && !isProcessingAnswer && !sessionExpired && (
        <div className="chatbot-input-container">
          {/* Suggested Questions list first (when input empty and before first message) */}
          {showSuggestions && suggestedQuestions.length > 0 && (
            <div className="repetitive-questions-suggestions">
              <div className="suggestions-header">💡 Suggested Questions:</div>
              <div className="suggestions-list">
                {suggestedQuestions.map((q) => (
                  <div
                    key={q.id}
                    className="suggestion-item"
                    onMouseDown={(e) => {
                      e.preventDefault(); // Prevent blur event from firing
                      handleSuggestionClick(q);
                    }}
                  >
                    <span className="suggestion-question">{q.question}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Smart Follow-ups: show after last bot message (including after first suggested question) */}
          {messages.length > 0 &&
            messages[messages.length - 1].sender === 'bot' &&
            messages[messages.length - 1].smartFollowUps &&
            messages[messages.length - 1].smartFollowUps.length > 0 &&
            input.trim() === '' && (
              <div
                className="smart-follow-ups"
                onWheel={(e) => {
                  const el = e.currentTarget;
                  if (el.scrollWidth <= el.clientWidth) return;
                  const dx = e.deltaX;
                  const dy = e.deltaY;
                  if (Math.abs(dx) >= Math.abs(dy)) {
                    el.scrollLeft += dx;
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                {messages[messages.length - 1].smartFollowUps.map((suggestion, idx) => (
                  <button
                    key={idx}
                    className={`smart-suggestion-btn color-${idx % 4}`}
                    onClick={() => {
                      setInput(suggestion);
                      // Single-click send logic
                      setTimeout(() => {
                        const sendBtn = document.querySelector('.chatbot-input button:last-child');
                        if (sendBtn) sendBtn.click();
                      }, 100);
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          <div className="chatbot-input">
            <button
              className={`mic-button ${isListening ? 'listening' : ''}`}
              onClick={startListening}
              onMouseDown={(e) => e.preventDefault()} // Prevent textarea blur
              title={isListening ? 'Stop recording' : (typeof window !== 'undefined' && window.self !== window.top ? 'Start voice input (may be blocked when embedded on another site)' : 'Start voice input')}
              aria-label={isListening ? 'Stop recording' : 'Start voice input'}
            >
              {isListening ? '🛑' : '🎤'}
            </button>
            <textarea
              ref={inputRef}
              placeholder={isListening ? "Listening... Speak now" : "Type your message..."}
              value={input}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              rows={1}
              style={{ resize: 'none', overflow: 'hidden', minHeight: '24px' }}
            />
            <button onClick={sendMessage}>Send</button>
          </div>
        </div>
      )}
      {sessionExpired && (
        <div className="chatbot-input disabled">
          <textarea
            placeholder="This chat has ended due to inactivity."
            value={""}
            disabled
            rows={1}
            style={{ resize: 'none', overflow: 'hidden', minHeight: '60px' }}
          />
          <button disabled>Send</button>
        </div>
      )}
      <div className="wetano-footer">
        Powered by{" "}
        <a
          className="wetano-footer-link"
          href="https://www.wetano.com"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Wetano Solutions (opens in new tab)"
        >
          Wetano Solutions
        </a>
      </div>
    </div>
  );
}
